import { Box, Button, Card, Grid, makeStyles, Typography } from "@material-ui/core";
import { ChevronLeft } from "@material-ui/icons";
import EventIcon from '@material-ui/icons/Event';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import { format } from 'date-fns';
import React from "react";
import { useHistory } from "react-router";
import { IMsalContext } from "../../../authentication/MsalContext";
import { useMsal } from "../../../authentication/MsalProvider";
import { Auction } from "../../../services/AuctionService";

const useStyles = makeStyles((theme) => ({
    card: {
        padding: theme.spacing(5),
        borderRadius: theme.spacing(2),
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
        textAlign: 'center',
        maxWidth: 700,
        margin: '0 auto',
    },
    icon: {
        fontSize: 64,
        color: theme.palette.primary.main,
        marginBottom: theme.spacing(2),
    },
    title: {
        fontWeight: 600,
        marginBottom: theme.spacing(2),
        color: theme.palette.text.primary,
    },
    description: {
        color: theme.palette.text.secondary,
        marginBottom: theme.spacing(3),
    },
    divider: {
        margin: theme.spacing(3, 0),
    },
    lotInfoBox: {
        backgroundColor: theme.palette.grey[50],
        padding: theme.spacing(3),
        borderRadius: theme.spacing(1),
        marginBottom: theme.spacing(3),
    },
    lotTitle: {
        fontWeight: 500,
        marginBottom: theme.spacing(2),
        color: theme.palette.text.primary,
    },
    endDateBox: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: theme.spacing(1),
        color: theme.palette.text.secondary,
    },
    dateIcon: {
        fontSize: 20,
    },
    button: {
        padding: theme.spacing(1.5, 4),
        borderRadius: theme.spacing(1),
        textTransform: 'none',
        fontSize: '1rem',
        fontWeight: 500,
    },
    buttonGroup: {
        display: 'flex',
        gap: theme.spacing(2),
        flexWrap: 'wrap',
        justifyContent: 'center',
    },
}));

interface LotEndedViewProps {
    auction: Auction;
}

const LotEndedView: React.FC<LotEndedViewProps> = ({ auction }) => {
    const classes = useStyles();
    const history = useHistory();

    const msal: IMsalContext = useMsal();

    const backToListings = () => {
        // Retrieve the saved query string..
        const queryString = localStorage.getItem('queryString');

        // Navigate back to the search page with the query string..
        history.push(`/Search${queryString ?? ''}`);
    };

    const goToBidHistory = () => {
        history.push('/BidHistory');
    };

    const formatEndDate = (date: Date | string) => {
        const endDate = typeof date === 'string' ? new Date(date) : date;
        return format(endDate, 'MMMM dd, yyyy \'at\' h:mm a');
    };

    return (
        <Grid container spacing={5} justifyContent="center">
            <Grid item xs={12}>
                <Card className={classes.card}>
                    <Box display="flex" flexDirection="column" alignItems="center">
                        <InfoOutlinedIcon className={classes.icon} />
                        <Typography variant="h4" className={classes.title}>
                            Lot Has Ended
                        </Typography>
                        <Typography variant="body1" className={classes.description}>
                            This auction lot is no longer available for bidding / viewing.
                        </Typography>

                        {/* <Divider className={classes.divider} style={{ width: '100%' }} /> */}

                        <Box className={classes.lotInfoBox} width="100%">
                            <Typography variant="h6" className={classes.lotTitle}>
                                {auction.title}
                            </Typography>
                            <Box className={classes.endDateBox}>
                                <EventIcon className={classes.dateIcon} />
                                <Typography variant="body2">
                                    Ended on {formatEndDate(auction.auctionEnd)}
                                </Typography>
                            </Box>
                        </Box>

                        <Typography variant="body2" className={classes.description}>
                            Browse our active listings to find other opportunities.
                        </Typography>

                        <Box className={classes.buttonGroup}>
                            <Button
                                variant="contained"
                                color="primary"
                                className={classes.button}
                                onClick={backToListings}
                            >
                                <ChevronLeft />
                                Back to Listings
                            </Button>

                            {msal.accounts.length > 0 && (
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    className={classes.button}
                                    onClick={goToBidHistory}
                                >
                                    View Bid History
                                </Button>
                            )}
                        </Box>
                    </Box>
                </Card>
            </Grid>
        </Grid>
    );
};

export default LotEndedView;